import { useCallback, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useProfile } from '@mint-lib/profile-helpers';
import { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import { toUserName } from '../../helpers/knowledge-base/userName.js';

type SharedFolderDraft = {
  name: string;
  description: string;
  grants: KnowledgeBase.SetFolderGrantsRequest;
};

const notify = (variant: 'success' | 'error', subTitle: string) =>
  enqueueSnackbar({ variant, subTitle, removable: true });

export const useFolderMutations = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { profileService } = useProfile();
  const profile = profileService.getProfileData()?.profile;

  const { mutateAsync: createFolder } = KnowledgeBase.createFolder();
  const { mutateAsync: setFolderGrants } = KnowledgeBase.setFolderGrants();

  // One flag for the whole hook: a caller only ever runs one mutation at a time.
  const [isPending, setIsPending] = useState(false);

  const run = useCallback(async <T>(task: () => Promise<T>) => {
    setIsPending(true);

    try {
      return await task();
    } finally {
      setIsPending(false);
    }
  }, []);

  const createPrivateBucket = useCallback(
    () =>
      run(async () => {
        const ownerName =
          toUserName({
            firstName: profile?.firstName,
            lastName: profile?.lastName,
          }) || `User ${profile?.id}`;

        try {
          const folder = await createFolder({
            data: {
              name: `${ownerName}’s private folder`,
              parentFolderId: null,
              isPrivate: true,
            },
          });

          return folder.folderId;
        } catch {
          notify(
            'error',
            t('Something went wrong. The folder could not be created'),
          );

          return null;
        }
      }),
    [run, createFolder, profile?.firstName, profile?.lastName, profile?.id, t],
  );

  const createSharedFolder = useCallback(
    ({ name, description, grants }: SharedFolderDraft) =>
      run(async () => {
        let folderId: string | null = null;

        try {
          const folder = await createFolder({
            data: {
              name: name.trim(),
              description: description.trim() || null,
              parentFolderId: null,
              isPrivate: false,
            },
          });

          folderId = folder.folderId;

          await setFolderGrants({ urlParams: { folderId }, data: grants });

          notify('success', t('Folder created successfully'));

          return true;
        } catch {
          notify(
            'error',
            folderId
              ? t('Folder created, but its access could not be saved')
              : t('Something went wrong. The folder could not be created'),
          );

          return Boolean(folderId);
        }
      }),
    [run, createFolder, setFolderGrants, t],
  );

  const updateFolderAccess = useCallback(
    (folderId: string, data: KnowledgeBase.SetFolderGrantsRequest) =>
      run(async () => {
        try {
          await setFolderGrants({ urlParams: { folderId }, data });

          notify('success', t('Access updated'));

          return true;
        } catch {
          notify(
            'error',
            t('Something went wrong. Access could not be updated'),
          );

          return false;
        }
      }),
    [run, setFolderGrants, t],
  );

  return {
    createPrivateBucket,
    createSharedFolder,
    updateFolderAccess,
    isPending,
  };
};
